/**
 * Central `handleUnauthorized` dispatch (E10-F01-T05).
 *
 * Parity with the legacy SPA `core.js` `handleUnauthorized()`: a SINGLE reaction
 * to "the session is no longer valid", triggered from exactly two places —
 *   1. the api-client, when a response is HTTP 401 OR normalizes to an
 *      `UNAUTHENTICATED` error (`isUnauthorizedError`), i.e. the new-REST 401 and
 *      the old-shape `{ success:false, message:'unauthorized' }` both land here;
 *   2. the auth layer, when a session-restore validation (`GET /auth/me`) fails.
 *
 * The api-client lives BELOW the React tree (it is plain functions, no hooks), so
 * it cannot call into `useAuth` directly. Instead the auth layer *registers* its
 * handler here once on mount, and the client fires it through this tiny registry.
 * This keeps the "no throw in UI" guarantee: the client still returns a normal
 * `ApiResult`, and the side effect (clear session + show login) is a registered
 * callback rather than control-flow smuggled through an exception.
 *
 * The base behaviour (`baseHandleUnauthorized`) — clearing the session/cache —
 * runs even when no React handler is registered (e.g. a 401 during the very
 * first restore call, before mount), so a stale token is ALWAYS dropped.
 */
import { clearSession, getCachedUser, getSessionToken } from '../api/session.js';

/** A reaction to an unauthorized signal (e.g. flip the app to the login view). */
export type UnauthorizedHandler = () => void;

let registered: UnauthorizedHandler | null = null;

/**
 * Register the app-level unauthorized reaction (the auth layer calls this on
 * mount). Returns an unsubscribe so a remount/unmount cannot leave a stale
 * closure wired to the client. Idempotent per-handler.
 */
export function registerUnauthorizedHandler(handler: UnauthorizedHandler): () => void {
  registered = handler;
  return () => {
    if (registered === handler) registered = null;
  };
}

/**
 * Always-on base reaction: drop the session token + cached user (the same keys
 * `logout` clears). Runs regardless of whether a React handler is registered, so
 * an unauthorized response during cold-start restore still purges stale state.
 */
export function baseHandleUnauthorized(): void {
  clearSession();
}

/**
 * Fire the unauthorized flow: clear local session/cache, then notify the
 * registered app handler (show login, hide main-app) if one is mounted. Safe to
 * call from anywhere; a throwing handler is swallowed so it can never break the
 * caller's normal return path.
 */
export function handleUnauthorized(): void {
  baseHandleUnauthorized();
  if (registered) {
    try {
      registered();
    } catch {
      /* a UI reaction must never break the request that triggered it */
    }
  }
}

/* --------------------------------------------------------------------------- *
 * E12-F08-T13 — 403 password_reset_required → the reset screen
 * --------------------------------------------------------------------------- */

/** Reaction to the reset gate: show the reset view, pre-filled with `email`. */
export type PasswordResetRequiredHandler = (email: string | null) => void;

let resetRegistered: PasswordResetRequiredHandler | null = null;

/** Register the app-level reset reaction (useAuth, on mount). Returns unsubscribe. */
export function registerPasswordResetRequiredHandler(
  handler: PasswordResetRequiredHandler,
): () => void {
  resetRegistered = handler;
  return () => {
    if (resetRegistered === handler) resetRegistered = null;
  };
}

/**
 * Fire the reset-gate flow ONCE per session: the api-client calls this on a 403
 * whose `error.details.reason === 'password_reset_required'`. Reads the cached
 * user's email first (to pre-fill the reset form), then drops the session — so
 * the concurrent 403s of the same burst (e.g. the clients + projects warm-up)
 * find no session and do nothing. Never throws.
 */
export function handlePasswordResetRequired(): void {
  if (!getSessionToken()) return; // already handled for this session
  const email = getCachedUser()?.email ?? null;
  clearSession();
  if (resetRegistered) {
    try {
      resetRegistered(email);
    } catch {
      /* a UI reaction must never break the request that triggered it */
    }
  }
}

/** Test-only reset of the registry. */
export function __resetUnauthorizedForTests(): void {
  registered = null;
  resetRegistered = null;
}
